import { useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Prism as SyntaxHighlighter } from 'react-syntax-highlighter';
import { vscDarkPlus } from 'react-syntax-highlighter/dist/esm/styles/prism';
import { FUNDAMENTALS, type InterviewQA, type QuizQuestion } from '../data/fundamentals';

type Section = 'concepts' | 'usecases' | 'interview-qa' | 'code' | 'quiz' | 'exercises';

const SECTIONS: { key: Section; icon: string; label: string }[] = [
  { key: 'concepts', icon: 'lightbulb', label: 'Fundamentos' },
  { key: 'usecases', icon: 'cases', label: 'Casos de Uso' },
  { key: 'interview-qa', icon: 'forum', label: 'Preguntas de Entrevista' },
  { key: 'code', icon: 'code', label: 'Código' },
  { key: 'quiz', icon: 'quiz', label: 'Quiz' },
  { key: 'exercises', icon: 'fitness_center', label: 'Ejercicios' },
];

const DIFFICULTY_STYLES = {
  easy: 'bg-emerald-500/10 text-emerald-400 border-emerald-500/20',
  medium: 'bg-amber-500/10 text-amber-400 border-amber-500/20',
  hard: 'bg-red-500/10 text-red-400 border-red-500/20',
};

const DIFFICULTY_LABELS = { easy: 'Fácil', medium: 'Medio', hard: 'Difícil' };

function DifficultyBadge({ difficulty }: { difficulty: 'easy' | 'medium' | 'hard' }) {
  return (
    <span
      className={`rounded border px-2 py-0.5 text-[10px] font-bold uppercase tracking-widest ${DIFFICULTY_STYLES[difficulty]}`}
    >
      {DIFFICULTY_LABELS[difficulty]}
    </span>
  );
}

function ConceptsSection({ concepts }: { concepts: typeof FUNDAMENTALS[0]['concepts'] }) {
  return (
    <div className="space-y-4">
      {concepts.map((concept) => (
        <div key={concept.title} className="rounded-xl bg-surface-container-low p-5">
          <div className="mb-2 flex flex-wrap items-start justify-between gap-2">
            <h3 className="font-bold text-on-surface">{concept.title}</h3>
            {concept.tags ? (
              <div className="flex flex-wrap gap-1">
                {concept.tags.map((tag) => (
                  <span
                    key={tag}
                    className="rounded bg-surface-container-high px-2 py-0.5 text-[10px] font-bold uppercase tracking-widest text-on-surface-variant"
                  >
                    {tag}
                  </span>
                ))}
              </div>
            ) : null}
          </div>
          <p className="text-sm leading-relaxed text-on-surface-variant">{concept.description}</p>
        </div>
      ))}
    </div>
  );
}

function UseCasesSection({ useCases }: { useCases: typeof FUNDAMENTALS[0]['useCases'] }) {
  return (
    <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
      {useCases.map((uc) => (
        <div key={uc.title} className="rounded-xl bg-surface-container-low p-5">
          <div className="mb-2 flex items-center gap-2">
            <span className="material-symbols-outlined text-base text-primary">check_circle</span>
            <h3 className="font-bold text-on-surface">{uc.title}</h3>
          </div>
          <p className="text-sm leading-relaxed text-on-surface-variant">{uc.description}</p>
        </div>
      ))}
    </div>
  );
}

function InterviewQASection({ qa }: { qa: InterviewQA[] }) {
  const [open, setOpen] = useState<number | null>(null);

  return (
    <div className="space-y-3">
      {qa.map((item, i) => (
        <div key={i} className="rounded-xl bg-surface-container-low overflow-hidden">
          <button
            onClick={() => setOpen(open === i ? null : i)}
            className="flex w-full items-start gap-3 p-5 text-left transition-colors hover:bg-surface-container"
          >
            <span className="material-symbols-outlined mt-0.5 shrink-0 text-base text-primary">
              {open === i ? 'expand_less' : 'expand_more'}
            </span>
            <div className="min-w-0 flex-1">
              <div className="flex flex-wrap items-center gap-2">
                <p className="font-medium text-on-surface">{item.question}</p>
                <DifficultyBadge difficulty={item.difficulty} />
              </div>
            </div>
          </button>
          {open === i ? (
            <div className="border-t border-outline-variant/10 bg-surface-container/50 px-5 pb-5 pt-4">
              <p className="text-sm leading-relaxed text-on-surface-variant">{item.answer}</p>
            </div>
          ) : null}
        </div>
      ))}
    </div>
  );
}

const LANGUAGE_LABELS: Record<string, string> = {
  typescript: 'TypeScript',
  javascript: 'JavaScript',
  python: 'Python',
  bash: 'Bash',
  sql: 'SQL',
  json: 'JSON',
  yaml: 'YAML',
};

function CopyButton({ code }: { code: string }) {
  const [copied, setCopied] = useState(false);

  function handleCopy() {
    navigator.clipboard.writeText(code).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    });
  }

  return (
    <button
      onClick={handleCopy}
      className="flex items-center gap-1.5 rounded px-2 py-1 text-xs text-slate-400 transition-colors hover:bg-white/10 hover:text-slate-200"
    >
      <span className="material-symbols-outlined text-sm">
        {copied ? 'check' : 'content_copy'}
      </span>
      {copied ? 'Copiado' : 'Copiar'}
    </button>
  );
}

function CodeSection({ examples }: { examples: typeof FUNDAMENTALS[0]['codeExamples'] }) {
  const [active, setActive] = useState(0);
  const example = examples[active];

  return (
    <div className="space-y-4">
      {/* Tabs de ejemplos */}
      <div className="flex flex-wrap gap-2">
        {examples.map((ex, i) => (
          <button
            key={i}
            onClick={() => setActive(i)}
            className={`rounded-lg px-3 py-2 text-sm font-medium transition-colors ${
              active === i
                ? 'bg-surface-container-high text-on-surface'
                : 'bg-surface-container-low text-on-surface-variant hover:text-on-surface'
            }`}
          >
            {ex.title}
          </button>
        ))}
      </div>

      {/* Editor card */}
      <div className="overflow-hidden rounded-xl border border-white/5 shadow-xl">
        {/* Descripción */}
        <div className="border-b border-white/5 bg-surface-container-low px-5 py-4">
          <h3 className="font-bold text-on-surface">{example.title}</h3>
          <p className="mt-1 text-sm text-on-surface-variant">{example.description}</p>
        </div>

        {/* Barra de título estilo editor */}
        <div className="flex items-center justify-between bg-[#1e1e1e] px-4 py-2">
          {/* Dots estilo macOS */}
          <div className="flex items-center gap-1.5">
            <span className="h-3 w-3 rounded-full bg-[#ff5f57]" />
            <span className="h-3 w-3 rounded-full bg-[#febc2e]" />
            <span className="h-3 w-3 rounded-full bg-[#28c840]" />
          </div>
          {/* Nombre de archivo */}
          <span className="text-xs text-slate-400">
            example.{example.language === 'python' ? 'py' : example.language === 'typescript' ? 'ts' : example.language === 'bash' ? 'sh' : example.language}
          </span>
          {/* Badge de lenguaje + copiar */}
          <div className="flex items-center gap-2">
            <span className="rounded bg-white/5 px-2 py-0.5 text-[10px] font-bold uppercase tracking-widest text-slate-400">
              {LANGUAGE_LABELS[example.language] ?? example.language}
            </span>
            <CopyButton code={example.code} />
          </div>
        </div>

        {/* Código con highlighting */}
        <SyntaxHighlighter
          language={example.language === 'typescript' ? 'tsx' : example.language}
          style={vscDarkPlus}
          showLineNumbers
          lineNumberStyle={{ color: '#4e5465', minWidth: '2.5em', paddingRight: '1em', userSelect: 'none' }}
          customStyle={{
            margin: 0,
            borderRadius: 0,
            background: '#1e1e1e',
            padding: '1.25rem 1.25rem 1.25rem 0',
            fontSize: '0.8125rem',
            lineHeight: '1.65',
          }}
          codeTagProps={{ style: { fontFamily: '"JetBrains Mono", "Fira Code", "Cascadia Code", Consolas, monospace' } }}
        >
          {example.code}
        </SyntaxHighlighter>
      </div>
    </div>
  );
}

interface QuizState {
  answers: (number | null)[];
  submitted: boolean;
}

function QuizSection({ questions }: { questions: QuizQuestion[] }) {
  const [state, setState] = useState<QuizState>({
    answers: questions.map(() => null),
    submitted: false,
  });

  const score = state.answers.filter((a, i) => a === questions[i].correctIndex).length;
  const allAnswered = state.answers.every((a) => a !== null);

  function selectAnswer(qi: number, oi: number) {
    if (state.submitted) return;
    setState((prev) => {
      const answers = [...prev.answers];
      answers[qi] = oi;
      return { ...prev, answers };
    });
  }

  function reset() {
    setState({ answers: questions.map(() => null), submitted: false });
  }

  return (
    <div className="space-y-6">
      {state.submitted ? (
        <div className="rounded-xl bg-surface-container-low p-5">
          <div className="flex items-center gap-4">
            <div
              className={`flex h-16 w-16 items-center justify-center rounded-full text-xl font-bold ${
                score >= questions.length * 0.7
                  ? 'bg-emerald-500/10 text-emerald-400'
                  : 'bg-amber-500/10 text-amber-400'
              }`}
            >
              {score}/{questions.length}
            </div>
            <div>
              <p className="font-bold text-on-surface">
                {score >= questions.length * 0.7 ? '¡Muy bien!' : 'Sigue practicando'}
              </p>
              <p className="text-sm text-on-surface-variant">
                {Math.round((score / questions.length) * 100)}% de respuestas correctas
              </p>
            </div>
            <button
              onClick={reset}
              className="ml-auto rounded-lg bg-surface-container px-4 py-2 text-sm font-medium text-on-surface transition-colors hover:bg-surface-container-high"
            >
              Reintentar
            </button>
          </div>
        </div>
      ) : null}

      {questions.map((q, qi) => {
        const selected = state.answers[qi];
        const isCorrect = selected === q.correctIndex;

        return (
          <div key={qi} className="rounded-xl bg-surface-container-low p-5">
            <p className="mb-4 font-medium text-on-surface">
              <span className="mr-2 text-primary">{qi + 1}.</span>
              {q.question}
            </p>
            <div className="space-y-2">
              {q.options.map((opt, oi) => {
                let optStyle =
                  'border border-outline-variant/20 bg-surface-container text-on-surface-variant hover:border-primary/30 hover:text-on-surface';

                if (state.submitted) {
                  if (oi === q.correctIndex) {
                    optStyle = 'border border-emerald-500/30 bg-emerald-500/10 text-emerald-400';
                  } else if (oi === selected && !isCorrect) {
                    optStyle = 'border border-red-500/30 bg-red-500/10 text-red-400';
                  } else {
                    optStyle = 'border border-outline-variant/10 bg-surface-container/50 text-on-surface-variant opacity-50';
                  }
                } else if (selected === oi) {
                  optStyle = 'border border-primary bg-primary/10 text-primary';
                }

                return (
                  <button
                    key={oi}
                    onClick={() => selectAnswer(qi, oi)}
                    disabled={state.submitted}
                    className={`flex w-full items-center gap-3 rounded-lg px-4 py-3 text-left text-sm transition-colors ${optStyle}`}
                  >
                    <span className="shrink-0 font-bold">
                      {String.fromCharCode(65 + oi)}.
                    </span>
                    {opt}
                    {state.submitted && oi === q.correctIndex ? (
                      <span className="material-symbols-outlined ml-auto text-base text-emerald-400">
                        check_circle
                      </span>
                    ) : null}
                    {state.submitted && oi === selected && !isCorrect ? (
                      <span className="material-symbols-outlined ml-auto text-base text-red-400">
                        cancel
                      </span>
                    ) : null}
                  </button>
                );
              })}
            </div>
            {state.submitted ? (
              <div className="mt-3 rounded-lg bg-surface-container/60 p-3">
                <p className="text-xs leading-relaxed text-on-surface-variant">
                  <span className="font-bold text-on-surface">Explicación: </span>
                  {q.explanation}
                </p>
              </div>
            ) : null}
          </div>
        );
      })}

      {!state.submitted ? (
        <button
          onClick={() => setState((prev) => ({ ...prev, submitted: true }))}
          disabled={!allAnswered}
          className="w-full rounded-xl bg-primary py-3 text-sm font-bold text-on-primary transition-opacity disabled:opacity-40"
        >
          {allAnswered ? 'Ver resultados' : `Responde todas las preguntas (${state.answers.filter((a) => a !== null).length}/${questions.length})`}
        </button>
      ) : null}
    </div>
  );
}

function ExercisesSection({ exercises }: { exercises: typeof FUNDAMENTALS[0]['exercises'] }) {
  const [openHints, setOpenHints] = useState<number | null>(null);

  return (
    <div className="space-y-4">
      {exercises.map((ex, i) => (
        <div key={i} className="rounded-xl bg-surface-container-low overflow-hidden">
          <div className="p-5">
            <div className="mb-3 flex flex-wrap items-start justify-between gap-2">
              <h3 className="font-bold text-on-surface">{ex.title}</h3>
              <DifficultyBadge difficulty={ex.difficulty} />
            </div>
            <p className="text-sm leading-relaxed text-on-surface-variant">{ex.description}</p>
            <div className="mt-3 flex flex-wrap gap-1.5">
              {ex.tags.map((tag) => (
                <span
                  key={tag}
                  className="rounded bg-surface-container-high px-2 py-0.5 text-[10px] font-bold uppercase tracking-widest text-on-surface-variant"
                >
                  {tag}
                </span>
              ))}
            </div>
          </div>
          <div className="border-t border-outline-variant/10">
            <button
              onClick={() => setOpenHints(openHints === i ? null : i)}
              className="flex w-full items-center gap-2 px-5 py-3 text-left text-sm font-medium text-on-surface-variant transition-colors hover:text-on-surface"
            >
              <span className="material-symbols-outlined text-base">tips_and_updates</span>
              {openHints === i ? 'Ocultar pistas' : `Ver pistas (${ex.hints.length})`}
              <span className="material-symbols-outlined ml-auto text-base">
                {openHints === i ? 'expand_less' : 'expand_more'}
              </span>
            </button>
            {openHints === i ? (
              <div className="border-t border-outline-variant/10 bg-surface-container/50 px-5 pb-4 pt-3">
                <ul className="space-y-2">
                  {ex.hints.map((hint, hi) => (
                    <li key={hi} className="flex items-start gap-2 text-sm text-on-surface-variant">
                      <span className="material-symbols-outlined mt-0.5 shrink-0 text-sm text-primary">
                        arrow_right
                      </span>
                      {hint}
                    </li>
                  ))}
                </ul>
              </div>
            ) : null}
          </div>
        </div>
      ))}
    </div>
  );
}

export default function FundamentalsDetailPage() {
  const { slug } = useParams<{ slug: string }>();
  const navigate = useNavigate();
  const [section, setSection] = useState<Section>('concepts');

  const tech = FUNDAMENTALS.find((f) => f.slug === slug);

  if (!tech) {
    return (
      <div className="flex min-h-[calc(100svh-3.5rem)] items-center justify-center">
        <div className="text-center">
          <p className="font-semibold text-on-surface">Tecnología no encontrada</p>
          <button
            onClick={() => navigate('/training')}
            className="mt-3 text-sm text-primary hover:underline"
          >
            Volver a Training
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="mx-auto max-w-4xl space-y-6 p-4 md:p-8">
      {/* Header */}
      <div>
        <button
          onClick={() => navigate('/training')}
          className="mb-4 flex items-center gap-1.5 text-sm text-on-surface-variant transition-colors hover:text-on-surface"
        >
          <span className="material-symbols-outlined text-base">arrow_back</span>
          Training
        </button>
        <div className="flex items-center gap-3">
          <div className="flex h-12 w-12 shrink-0 items-center justify-center rounded-2xl bg-surface-container-high">
            <span className={`material-symbols-outlined text-2xl ${tech.color}`}>{tech.icon}</span>
          </div>
          <div>
            <p className="text-[10px] font-bold uppercase tracking-[0.2em] text-primary">Fundamentals</p>
            <h1 className="text-2xl font-bold text-on-surface">{tech.title}</h1>
          </div>
        </div>
        <p className="mt-2 text-sm text-on-surface-variant">{tech.shortDescription}</p>
      </div>

      {/* Section tabs — horizontal scroll on mobile */}
      <div className="overflow-x-auto">
        <div className="flex min-w-max gap-1 rounded-2xl border border-outline-variant/10 bg-surface-container-low p-1">
          {SECTIONS.map((s) => (
            <button
              key={s.key}
              onClick={() => setSection(s.key)}
              className={`flex items-center gap-1.5 rounded-xl px-3 py-2 text-sm font-medium whitespace-nowrap transition-colors ${
                section === s.key
                  ? 'bg-surface-container-high text-on-surface shadow-sm'
                  : 'text-on-surface-variant hover:text-on-surface'
              }`}
            >
              <span className="material-symbols-outlined text-base">{s.icon}</span>
              <span className="hidden sm:inline">{s.label}</span>
            </button>
          ))}
        </div>
      </div>

      {/* Section title */}
      <div>
        <h2 className="text-lg font-bold text-on-surface">
          {SECTIONS.find((s) => s.key === section)?.label}
        </h2>
        {section === 'quiz' ? (
          <p className="mt-1 text-sm text-on-surface-variant">
            Responde todas las preguntas y comprueba tu nivel de {tech.title}.
          </p>
        ) : null}
        {section === 'exercises' ? (
          <p className="mt-1 text-sm text-on-surface-variant">
            Ejercicios comunes en entrevistas técnicas de {tech.title}.
          </p>
        ) : null}
      </div>

      {/* Section content */}
      {section === 'concepts' ? <ConceptsSection concepts={tech.concepts} /> : null}
      {section === 'usecases' ? <UseCasesSection useCases={tech.useCases} /> : null}
      {section === 'interview-qa' ? <InterviewQASection qa={tech.interviewQA} /> : null}
      {section === 'code' ? <CodeSection examples={tech.codeExamples} /> : null}
      {section === 'quiz' ? <QuizSection questions={tech.quiz} /> : null}
      {section === 'exercises' ? <ExercisesSection exercises={tech.exercises} /> : null}
    </div>
  );
}
